import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';

@Component({
	selector: 'app-section-card',
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<div class="card">
			<div class="header" (click)="toggle()">
				<i class="pi text-wb-muted chevron"
					[class.pi-chevron-right]="!open()"
					[class.pi-chevron-down]="open()"></i>
				<span class="title-group">
					<span class="title">{{ title() }}</span>
					@if (showBadge()) {
						<span class="wb-count-badge">{{ badge() }}</span>
					}
				</span>
				<ng-content select="[actions]" />
			</div>
			@if (open()) {
				<div class="body">
					<ng-content />
				</div>
			}
		</div>
	`,
	styleUrl: './section-card.scss',
})
export class SectionCardComponent {
	title = input.required<string>();
	badge = input<string | number | null>(null);
	collapsed = input(true);
	/** Bump this (e.g. a counter) to force the card open from a parent/ancestor. */
	expandRequest = input(0);

	// This is the only visible signal that a collapsed section (Images/Scenes/
	// Chapters/Audio Tracks) has anything inside before it's expanded, so it
	// must show from 1 upward, not just when there's more than one.
	protected showBadge = computed(() => {
		const b = this.badge();
		if (b === null) return false;
		return typeof b === 'number' ? b > 0 : true;
	});

	protected open = signal(false);
	private openInitialized = false;

	constructor() {
		// Single effect governs `open` so the initial `collapsed` read and later
		// `expandRequest` bumps can't race each other (they previously lived in
		// a queueMicrotask + a separate effect, and whichever ran last silently
		// clobbered the other's write).
		effect(() => {
			const forceOpen = this.expandRequest() > 0;
			if (!this.openInitialized) {
				this.openInitialized = true;
				this.open.set(forceOpen || !this.collapsed());
				return;
			}
			if (forceOpen) this.open.set(true);
		});
	}

	protected toggle(): void {
		this.open.update(v => !v);
	}
}
